'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function QuoteContactForm() {
  const [formData, setFormData] = useState({
    projectName: '',
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    projectDescription: '',
    material: '',
    quantity: '',
    timeline: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Quote contact submitted:', formData);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12 text-center">
        <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-success" />
        </div>
        <h2 className="text-3xl font-headline font-bold text-foreground mb-4">
          Quote Request Received!
        </h2>
        <p className="text-lg text-muted-foreground mb-8">
          Thank you for your quote request. Our team will review your requirements and respond within 2 hours.
        </p>
        <button
          onClick={() => setIsSubmitted(false)}
          className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-cta font-bold hover:opacity-90 transition-all duration-base"
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12">
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
            <Icon name="DocumentTextIcon" size={24} variant="solid" className="text-primary" />
          </div>
          <h2 className="text-3xl font-headline font-bold text-foreground">
            Request a Quote
          </h2>
        </div>
        <p className="text-muted-foreground font-body">
          Provide your project details and our technical team will prepare a detailed quote for you.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Project Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.projectName}
              onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
              placeholder="e.g., Custom Enclosure Manufacturing"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Company Name
            </label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              placeholder="Your company name"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Contact Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.contactName}
              onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
              placeholder="Your full name"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Email Address <span className="text-error">*</span>
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your.email@company.com"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Phone Number <span className="text-error">*</span>
            </label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(555) 123-4567"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Material Type
            </label>
            <input
              type="text"
              value={formData.material}
              onChange={(e) => setFormData({ ...formData, material: e.target.value })}
              placeholder="e.g., Aluminum 5052, Stainless 316L"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Quantity
            </label>
            <input
              type="text"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              placeholder="e.g., 100 units"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-base"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-cta font-semibold text-foreground">
              Timeline
            </label>
            <input
              type="text"
              value={formData.timeline}
              onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
              placeholder="e.g., 2-3 weeks"
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-base"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-cta font-semibold text-foreground">
            Project Description <span className="text-error">*</span>
          </label>
          <textarea
            required
            value={formData.projectDescription}
            onChange={(e) => setFormData({ ...formData, projectDescription: e.target.value })}
            placeholder="Describe your project requirements, specifications, and any special considerations..."
            rows={5}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-base resize-none"
          />
        </div>

        <div className="bg-accent/10 rounded-xl p-6 border border-accent/20">
          <div className="flex items-start space-x-3">
            <Icon name="InformationCircleIcon" size={24} variant="solid" className="text-accent flex-shrink-0" />
            <div className="space-y-2">
              <div className="text-sm font-cta font-semibold text-foreground">
                For detailed quotes with technical drawings
              </div>
              <div className="text-sm text-muted-foreground font-body">
                Use our <a href="/smart-quote-portal" className="text-primary hover:underline font-semibold">Smart Quote Portal</a> to upload CAD files and get more accurate pricing.
              </div>
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-lg font-cta font-bold hover:opacity-90 transition-all duration-base shadow-lg flex items-center justify-center space-x-2"
        >
          <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
          <span>Submit Quote Request</span>
        </button>
      </form>
    </div>
  );
}